import {
  Box,
  Button,
  Image,
  Input,
  InputGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useStytch, useStytchSession } from "@stytch/react";
import { StytchAPIError } from "@stytch/vanilla-js";
import { Mail } from "lucide-react";
import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SkyscraperImg from "../../assets/skyscraper.jpg";
import LoginModal from "../../components/LoginModal";
import PalommaLogoWithText from "../../components/PalommaLogoWithText";
import { ROUTES } from "../../config/routes";

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const Login = (): JSX.Element => {
  const stytch = useStytch();
  const [isOpenLoginModal, setIsOpenLoginModal] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const { session } = useStytchSession();
  const navigate = useNavigate();
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (session) navigate(ROUTES.DASHBOARD.HOME);
  }, [session, navigate]);

  const sendEmailMagicLink = async (): Promise<void> => {
    setErrorCode(null);
    try {
      await stytch.magicLinks.email.send(email, { locale: "es" });
    } catch (error) {
      if (error instanceof StytchAPIError && "status_code" in error)
        setErrorCode(error.status_code);

      console.error("Error sending magic link:", error);
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setEmail(value);
    setIsEmailValid(validateEmail(value));
  };

  const handleClick = async () => {
    setIsLoading(true);
    await sendEmailMagicLink();
    setIsLoading(false);
    setIsOpenLoginModal(true);
  };

  return (
    <>
      <Box display="flex" height="100vh">
        <Image src={SkyscraperImg} flexGrow={1} flexBasis="50%" />
        <VStack
          alignItems={"center"}
          gap={8}
          justifyContent={"center"}
          alignSelf={"stretch"}
          flexGrow={1}
          flexBasis="50%"
          marginX={10}
          height="100%"
        >
          <Box
            maxH={"350px"}
            maxW={"350px"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <PalommaLogoWithText />
          </Box>
          <Box
            display={"flex"}
            flexDirection={"column"}
            alignItems={"center"}
            gap={8}
          >
            <Text
              fontSize={"2rem"}
              fontWeight={600}
              lineHeight={"2.25rem"}
              width={"30rem"}
              textAlign={"center"}
            >
              Bienvenido al Portal Administrativo
            </Text>
            <Box
              display={"flex"}
              flexDirection={"column"}
              alignItems={"center"}
              gap={6}
              alignSelf={"stretch"}
            >
              <Text fontWeight={500} fontSize={"1.5rem"}>
                Validación de usuario
              </Text>
              <InputGroup startElement={<Mail size="1.2rem" color="#5D5C60" />}>
                <Input
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Ingresa tu correo electrónico"
                  _placeholder={{ color: "#5D5C60" }}
                  variant="outline"
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" && email) {
                      handleClick();
                    }
                  }}
                />
              </InputGroup>
              <Button
                onClick={handleClick}
                width="100%"
                disabled={!isEmailValid}
                loading={isLoading}
              >
                Continuar
              </Button>
            </Box>
          </Box>
        </VStack>
      </Box>
      <LoginModal
        open={isOpenLoginModal}
        onClose={() => {
          setIsOpenLoginModal(false);
          setEmail("");
          setIsEmailValid(false);
        }}
        onResendClick={sendEmailMagicLink}
        errorCode={errorCode}
      />
    </>
  );
};

export default Login;
